import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import { X, Plus, Image as ImageIcon, DollarSign, FileText, Link as LinkIcon, Layers, Ruler, Palette } from "lucide-react";
import ImageDropzone from "@/components/ImageDropzone";

const CATEGORIES = [
  { value: "mens", label: "Men's Apparel" },
  { value: "womens", label: "Women's Apparel" },
  { value: "apparel", label: "Hoodies & Outerwear" },
  { value: "prints", label: "Prints & Canvas" },
  { value: "diy_kits", label: "Paint-Your-Own Kits" },
  { value: "digital", label: "Digital Designs" },
  { value: "murals", label: "Murals" },
];

// Categories that show the colour-palette editor
const COLOURFUL_CATS = new Set(["mens", "womens", "apparel"]);

// Default palettes per category — used when Cam clicks "Load default palette"
const DEFAULT_PALETTES = {
  mens: [
    { value: "black",         label: "Black",         swatch: "#0A0A0A", border: "#333333" },
    { value: "bone",          label: "Bone",          swatch: "#E4D8C1", border: "#C9BE9F" },
    { value: "brown",         label: "Brown",         swatch: "#5B4A3B", border: "#5B4A3B" },
    { value: "burgundy",      label: "Burgundy",      swatch: "#8A1633", border: "#8A1633" },
    { value: "gravel",        label: "Gravel",        swatch: "#9BA0A2", border: "#7E8385" },
    { value: "lieutenant",    label: "Lieutenant",    swatch: "#6E7562", border: "#6E7562" },
    { value: "rust",          label: "Rust",          swatch: "#B4531A", border: "#B4531A" },
    { value: "sea_blue",      label: "Sea Blue",      swatch: "#2C5972", border: "#2C5972" },
    { value: "smokey_violet", label: "Smokey Violet", swatch: "#B097A5", border: "#B097A5" },
    { value: "white",         label: "White",         swatch: "#F5F5F5", border: "#CCCCCC" },
  ],
  womens: [
    { value: "black",         label: "Black",         swatch: "#0A0A0A", border: "#333333" },
    { value: "bone",          label: "Bone",          swatch: "#E4D8C1", border: "#C9BE9F" },
    { value: "heather_grey",  label: "Heather Grey",  swatch: "#BFC2C4", border: "#A3A6A8" },
    { value: "navy",          label: "Navy",          swatch: "#12224A", border: "#12224A" },
    { value: "smokey_violet", label: "Smokey Violet", swatch: "#B097A5", border: "#B097A5" },
    { value: "white",         label: "White",         swatch: "#F5F5F5", border: "#CCCCCC" },
  ],
  apparel: [
    { value: "blue_dusk",   label: "Blue Dusk",   swatch: "#25303B", border: "#25303B" },
    { value: "cherry_red",  label: "Cherry Red",  swatch: "#B5222C", border: "#B5222C" },
    { value: "deep_royal",  label: "Deep Royal",  swatch: "#0F2C74", border: "#0F2C74" },
    { value: "garnet",      label: "Garnet",      swatch: "#7C1E32", border: "#7C1E32" },
    { value: "gravel",      label: "Gravel",      swatch: "#9BA0A2", border: "#7E8385" },
    { value: "off_white",   label: "Off White",   swatch: "#F1E9DA", border: "#D6CEBF" },
    { value: "olive",       label: "Olive",       swatch: "#3E3A1E", border: "#3E3A1E" },
    { value: "pitch_black", label: "Pitch Black", swatch: "#0A0B0F", border: "#0A0B0F" },
    { value: "tan",         label: "Tan",         swatch: "#D2A87A", border: "#B98F63" },
  ],
};

const slugify = (label) =>
  label.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "colour";

const empty = {
  title: "", description: "", image_url: "", images: [], price: 0, category: "prints",
  external_url: "", is_visible: true, is_featured: false,
  variant_pricing: null, sort_order: 999,
};

const DEFAULT_VP = {
  sizes: [
    { value: "A4", label: "A4  ·  21 × 29.7 cm", price: 95 },
    { value: "A3", label: "A3  ·  29.7 × 42 cm", price: 152 },
    { value: "A2", label: "A2  ·  42 × 59.4 cm", price: 228 },
    { value: "A1", label: "A1  ·  59.4 × 84.1 cm", price: 342 },
    { value: "A0", label: "A0  ·  84.1 × 118.9 cm", price: 475 },
  ],
  frames: [
    { value: "unframed", label: "Unframed", add: 0 },
    { value: "framed", label: "Framed (satin black)", add: 90 },
  ],
};

// Reusable section header with a big icon
function SectionHeader({ icon: Icon, title, hint }) {
  return (
    <div className="flex items-start gap-3 mb-4">
      <div className="w-10 h-10 shrink-0 bg-[#FF4500]/10 border border-[#FF4500]/30 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#FF4500]" />
      </div>
      <div>
        <p className="font-display text-lg tracking-wider">{title}</p>
        {hint && <p className="text-xs text-white/50 mt-0.5">{hint}</p>}
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-white/40 mt-1.5">{hint}</p>}
    </div>
  );
}

export default function ProductFormDialog({ open, onOpenChange, product, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [newImg, setNewImg] = useState("");
  const isEdit = !!(product && product.id);

  useEffect(() => {
    if (product && product.id) {
      setForm({
        title: product.title || "",
        description: product.description || "",
        image_url: product.image_url || "",
        images: product.images && product.images.length ? [...product.images] : (product.image_url ? [product.image_url] : []),
        price: product.price || 0,
        category: product.category || "prints",
        external_url: product.external_url || "",
        is_visible: product.is_visible !== false,
        is_featured: !!product.is_featured,
        variant_pricing: product.variant_pricing || null,
        sort_order: product.sort_order ?? 999,
      });
    } else if (product && product.__presetCategory) {
      // "Add" clicked from a section — preseed the category
      setForm({ ...empty, category: product.__presetCategory });
    } else {
      setForm(empty);
    }
    setNewImg("");
  }, [product, open]);

  const set = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : (e?.target?.value ?? e);
    setForm({ ...form, [k]: v });
  };

  const addImage = () => {
    const url = newImg.trim();
    if (!url) return;
    if (form.images.includes(url)) { toast.error("That image is already added"); return; }
    const nextImages = [...form.images, url];
    setForm({ ...form, images: nextImages, image_url: form.image_url || url });
    setNewImg("");
  };

  const removeImage = (idx) => {
    const nextImages = form.images.filter((_, i) => i !== idx);
    const removed = form.images[idx];
    setForm({ ...form, images: nextImages, image_url: form.image_url === removed ? (nextImages[0] || "") : form.image_url });
  };

  const makePrimary = (idx) => {
    setForm({ ...form, image_url: form.images[idx] });
    toast.success("Primary image set");
  };

  const save = async () => {
    if (!form.title) { toast.error("Please enter a title"); return; }
    if (!form.image_url && form.images.length === 0) { toast.error("Please add at least one image"); return; }
    setSaving(true);
    try {
      const primary = form.image_url || form.images[0];
      const allImages = form.images.length ? form.images : [primary];
      const payload = {
        title: form.title,
        description: form.description,
        image_url: primary,
        images: allImages,
        price: parseFloat(form.price) || 0,
        category: form.category,
        external_url: form.external_url,
        is_visible: form.is_visible,
        is_featured: form.is_featured,
        variant_pricing: form.variant_pricing,
        sort_order: parseInt(form.sort_order, 10) || 999,
      };
      if (isEdit) {
        await adminApi.updateProduct(product.id, payload);
        toast.success("Product updated");
      } else {
        await adminApi.createProduct({ ...payload, source: "manual" });
        toast.success("Product created");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="product-form-dialog" className="max-w-3xl bg-[#0F0F0F] border border-white/10 text-white p-0 max-h-[92vh] overflow-hidden flex flex-col">
        <VisuallyHidden>
          <DialogTitle>{isEdit ? `Edit ${form.title || "product"}` : "Add a new product"}</DialogTitle>
          <DialogDescription>Edit product title, images, pricing, description and visibility options.</DialogDescription>
        </VisuallyHidden>
        {/* Sticky header */}
        <div className="sticky top-0 z-10 bg-[#0F0F0F]/95 backdrop-blur border-b border-white/10 px-6 md:px-8 py-5 flex items-center justify-between">
          <div>
            <p className="overline mb-1">{isEdit ? "Editing" : "New product"}</p>
            <h2 className="font-display text-2xl md:text-3xl tracking-wider">{isEdit ? form.title || "Untitled" : "Add a new artwork"}</h2>
          </div>
          <button data-testid="pf-close" onClick={() => onOpenChange(false)} className="w-9 h-9 border border-white/20 flex items-center justify-center hover:border-[#FF4500] hover:text-[#FF4500]">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto scroll-thin px-6 md:px-8 py-6 space-y-10">
          {/* 1. Basics */}
          <section>
            <SectionHeader icon={FileText} title="Basics" hint="What is it called and what type of piece?" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Field label="Title*" hint="e.g. Chaos Is Beautiful — Fine Art Print">
                  <input data-testid="pf-title" required className="cka-input" value={form.title} onChange={set("title")} />
                </Field>
              </div>
              <Field label="Category">
                <select data-testid="pf-category" className="cka-input" value={form.category} onChange={set("category")}>
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </Field>
            </div>
          </section>

          {/* 2. Images */}
          <section>
            <SectionHeader icon={ImageIcon} title="Images" hint="Add one or more images. The first (starred) is shown in the shop grid." />

            {form.images.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
                {form.images.map((url, idx) => {
                  const isPrimary = url === form.image_url;
                  return (
                    <div key={idx} data-testid={`pf-image-item-${idx}`} className={`relative aspect-square border ${isPrimary ? "border-[#FF4500]" : "border-white/10"} bg-black overflow-hidden group`}>
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                        {!isPrimary && (
                          <button data-testid={`pf-image-primary-${idx}`} onClick={() => makePrimary(idx)} className="text-xs uppercase tracking-widest px-3 py-1 border border-white hover:bg-white hover:text-black">
                            Make primary
                          </button>
                        )}
                        <button data-testid={`pf-image-remove-${idx}`} onClick={() => removeImage(idx)} className="text-xs uppercase tracking-widest px-3 py-1 border border-white/60 hover:border-red-500 hover:text-red-500">
                          Remove
                        </button>
                      </div>
                      {isPrimary && (
                        <div className="absolute top-2 left-2 bg-[#FF4500] px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold">Primary</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex gap-2">
              <input
                data-testid="pf-new-image"
                className="cka-input flex-1"
                placeholder="Paste image URL and press Add"
                value={newImg}
                onChange={(e) => setNewImg(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addImage(); } }}
              />
              <button data-testid="pf-add-image" onClick={addImage} className="btn-primary text-sm py-2 px-4">
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
            <div className="mt-3">
              <p className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Or drop a file to upload</p>
              <ImageDropzone
                value=""
                onUploaded={(url) => {
                  if (form.images.includes(url)) { toast.error("Already added"); return; }
                  const nextImages = [...form.images, url];
                  setForm({ ...form, images: nextImages, image_url: form.image_url || url });
                }}
                label="Drop image here or click to browse"
              />
            </div>
            {form.images.length === 0 && (
              <p className="text-xs text-white/40 mt-3">No images yet — drop a file above or paste a public image URL and press Add.</p>
            )}
          </section>

          {/* 3. Pricing & order */}
          <section>
            <SectionHeader icon={DollarSign} title="Pricing & order" hint="Base price (AUD) and the position it takes in the shop grid." />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Base price (AUD)" hint="For prints, this is your starting-from price.">
                <input data-testid="pf-price" type="number" step="0.01" className="cka-input" value={form.price} onChange={set("price")} />
              </Field>
              <Field label="Display order" hint="1 = first in the shop. 2 = second. And so on.">
                <input data-testid="pf-sort-order" type="number" step="1" className="cka-input" value={form.sort_order} onChange={set("sort_order")} />
              </Field>
            </div>
          </section>

          {/* 4. Description */}
          <section>
            <SectionHeader icon={FileText} title="Description" hint="The story or details shown on the product page." />
            <textarea data-testid="pf-description" rows={5} className="cka-input" value={form.description} onChange={set("description")} placeholder="Tell the story behind this piece..." />
          </section>

          {/* 5. External buy link */}
          <section>
            <SectionHeader icon={LinkIcon} title="Buy link" hint="Where shoppers finish the purchase (e.g. your Etsy listing)." />
            <input data-testid="pf-external" className="cka-input" value={form.external_url} onChange={set("external_url")} placeholder="https://www.etsy.com/au/listing/..." />
          </section>

          {/* 6. Visibility */}
          <section>
            <SectionHeader icon={Layers} title="Visibility" hint="Show or hide this item, and whether it's featured on the home page." />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="flex items-center gap-3 cursor-pointer border border-white/10 bg-[#141414] p-4 hover:border-white transition-colors">
                <input data-testid="pf-visible" type="checkbox" checked={form.is_visible} onChange={set("is_visible")} className="w-5 h-5 accent-[#FF4500]" />
                <div>
                  <p className="font-display tracking-wider">Visible in shop</p>
                  <p className="text-xs text-white/50">Uncheck to hide from public storefront</p>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer border border-white/10 bg-[#141414] p-4 hover:border-white transition-colors">
                <input data-testid="pf-featured" type="checkbox" checked={form.is_featured} onChange={set("is_featured")} className="w-5 h-5 accent-[#FF4500]" />
                <div>
                  <p className="font-display tracking-wider">Featured on home</p>
                  <p className="text-xs text-white/50">Show in the featured drops row</p>
                </div>
              </label>
            </div>
          </section>

          {/* 7. Print sizing */}
          {form.category === "prints" && (
            <section>
              <SectionHeader icon={Ruler} title="Print sizes & framing" hint="Set the exact sizes and prices this print is available in. Sizes are in centimetres." />

              {!form.variant_pricing ? (
                <div className="border border-dashed border-white/20 bg-[#141414] p-6 text-center">
                  <p className="text-white/60 mb-4">Currently using site-wide default sizing.</p>
                  <button data-testid="pf-vp-enable" onClick={() => setForm({ ...form, variant_pricing: JSON.parse(JSON.stringify(DEFAULT_VP)) })} className="btn-primary text-sm py-2 px-4">
                    Customise sizes for this print
                  </button>
                </div>
              ) : (
                <div className="border border-white/10 bg-[#141414] p-5 space-y-6">
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <p className="overline">Sizes (cm)</p>
                      <button data-testid="pf-vp-disable" onClick={() => setForm({ ...form, variant_pricing: null })} className="text-[10px] uppercase tracking-widest text-white/50 hover:text-white">
                        Reset to defaults
                      </button>
                    </div>
                    <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest text-white/40 mb-1 px-1">
                      <div className="col-span-2">Code</div>
                      <div className="col-span-7">Label shown to shopper</div>
                      <div className="col-span-2 text-right">Price</div>
                      <div className="col-span-1" />
                    </div>
                    {form.variant_pricing.sizes.map((s, i) => (
                      <div key={`size-${i}-${s.value}`} className="grid grid-cols-12 gap-2 mb-2 items-center">
                        <input data-testid={`pf-size-value-${i}`} className="cka-input col-span-2" value={s.value} onChange={(e) => { const v = [...form.variant_pricing.sizes]; v[i] = { ...v[i], value: e.target.value }; setForm({ ...form, variant_pricing: { ...form.variant_pricing, sizes: v } }); }} />
                        <input data-testid={`pf-size-label-${i}`} className="cka-input col-span-7" value={s.label} onChange={(e) => { const v = [...form.variant_pricing.sizes]; v[i] = { ...v[i], label: e.target.value }; setForm({ ...form, variant_pricing: { ...form.variant_pricing, sizes: v } }); }} />
                        <input data-testid={`pf-size-price-${i}`} type="number" step="0.01" className="cka-input col-span-2 text-right" value={s.price} onChange={(e) => { const v = [...form.variant_pricing.sizes]; v[i] = { ...v[i], price: parseFloat(e.target.value) || 0 }; setForm({ ...form, variant_pricing: { ...form.variant_pricing, sizes: v } }); }} />
                        <button type="button" onClick={() => { const v = form.variant_pricing.sizes.filter((_, x) => x !== i); setForm({ ...form, variant_pricing: { ...form.variant_pricing, sizes: v } }); }} className="col-span-1 text-white/50 hover:text-red-500">
                          <X className="w-4 h-4 mx-auto" />
                        </button>
                      </div>
                    ))}
                    <button type="button" data-testid="pf-add-size" onClick={() => setForm({ ...form, variant_pricing: { ...form.variant_pricing, sizes: [...form.variant_pricing.sizes, { value: "NEW", label: "New size · 0 × 0 cm", price: 0 }] } })} className="text-xs text-[#FF4500] uppercase tracking-widest hover:underline mt-2">
                      + Add another size
                    </button>
                  </div>
                  <div className="border-t border-white/10 pt-5">
                    <p className="overline mb-3">Framing options</p>
                    <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest text-white/40 mb-1 px-1">
                      <div className="col-span-2">Code</div>
                      <div className="col-span-7">Label</div>
                      <div className="col-span-2 text-right">Extra $</div>
                      <div className="col-span-1" />
                    </div>
                    {form.variant_pricing.frames.map((f, i) => (
                      <div key={`frame-${i}-${f.value}`} className="grid grid-cols-12 gap-2 mb-2 items-center">
                        <input data-testid={`pf-frame-value-${i}`} className="cka-input col-span-2" value={f.value} onChange={(e) => { const v = [...form.variant_pricing.frames]; v[i] = { ...v[i], value: e.target.value }; setForm({ ...form, variant_pricing: { ...form.variant_pricing, frames: v } }); }} />
                        <input data-testid={`pf-frame-label-${i}`} className="cka-input col-span-7" value={f.label} onChange={(e) => { const v = [...form.variant_pricing.frames]; v[i] = { ...v[i], label: e.target.value }; setForm({ ...form, variant_pricing: { ...form.variant_pricing, frames: v } }); }} />
                        <input data-testid={`pf-frame-add-${i}`} type="number" step="0.01" className="cka-input col-span-2 text-right" value={f.add} onChange={(e) => { const v = [...form.variant_pricing.frames]; v[i] = { ...v[i], add: parseFloat(e.target.value) || 0 }; setForm({ ...form, variant_pricing: { ...form.variant_pricing, frames: v } }); }} />
                        <button type="button" onClick={() => { const v = form.variant_pricing.frames.filter((_, x) => x !== i); setForm({ ...form, variant_pricing: { ...form.variant_pricing, frames: v } }); }} className="col-span-1 text-white/50 hover:text-red-500">
                          <X className="w-4 h-4 mx-auto" />
                        </button>
                      </div>
                    ))}
                    <button type="button" data-testid="pf-add-frame" onClick={() => setForm({ ...form, variant_pricing: { ...form.variant_pricing, frames: [...form.variant_pricing.frames, { value: "new", label: "New option", add: 0 }] } })} className="text-xs text-[#FF4500] uppercase tracking-widest hover:underline mt-2">
                      + Add another framing option
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* 8. Colour palette (apparel-like categories only) */}
          {COLOURFUL_CATS.has(form.category) && (() => {
            const colours = form.variant_pricing?.colours || [];
            const updateColours = (next) => {
              const base = form.variant_pricing || { sizes: [], frames: [], colours: [] };
              setForm({ ...form, variant_pricing: { ...base, colours: next } });
            };
            const loadDefault = () => {
              const preset = DEFAULT_PALETTES[form.category] || [];
              updateColours(JSON.parse(JSON.stringify(preset)));
            };
            const addColour = () => {
              updateColours([...colours, { value: `new_${colours.length + 1}`, label: "New colour", swatch: "#666666", border: "#666666" }]);
            };
            const removeColour = (i) => updateColours(colours.filter((_, x) => x !== i));
            const updateAt = (i, patch) => {
              const next = colours.map((c, x) => (x === i ? { ...c, ...patch } : c));
              updateColours(next);
            };
            return (
              <section data-testid="pf-colours-section">
                <SectionHeader
                  icon={Palette}
                  title="Colour palette"
                  hint="Add / rename / recolour the swatches shoppers pick from on this product's page."
                />
                {colours.length === 0 ? (
                  <div className="border border-dashed border-white/20 bg-[#141414] p-6 text-center">
                    <p className="text-white/60 mb-4">No colour options yet.</p>
                    <div className="flex flex-wrap gap-3 justify-center">
                      <button type="button" data-testid="pf-colours-load-default" onClick={loadDefault} className="btn-primary text-sm py-2 px-4">
                        Load default {form.category === "mens" ? "men's" : form.category === "womens" ? "women's" : "hoodie"} palette
                      </button>
                      <button type="button" data-testid="pf-colours-add-first" onClick={addColour} className="btn-ghost text-sm py-2 px-4">
                        Start from scratch
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="border border-white/10 bg-[#141414] p-5 space-y-3">
                    <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest text-white/40 mb-1 px-1">
                      <div className="col-span-1">Swatch</div>
                      <div className="col-span-5">Name shown to shopper</div>
                      <div className="col-span-3">Code (auto)</div>
                      <div className="col-span-2 text-center">Hex</div>
                      <div className="col-span-1" />
                    </div>
                    {colours.map((c, i) => (
                      <div key={`colour-${i}-${c.value}`} data-testid={`pf-colour-row-${i}`} className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-1 flex justify-center">
                          <span
                            className="w-8 h-8 rounded-full border-2 border-white/20"
                            style={{ background: c.swatch }}
                            title={c.label}
                          />
                        </div>
                        <input
                          data-testid={`pf-colour-label-${i}`}
                          className="cka-input col-span-5"
                          value={c.label}
                          onChange={(e) => {
                            const label = e.target.value;
                            updateAt(i, { label, value: slugify(label) });
                          }}
                        />
                        <input
                          data-testid={`pf-colour-value-${i}`}
                          className="cka-input col-span-3 text-white/50"
                          value={c.value}
                          readOnly
                          title="Auto-generated from name"
                        />
                        <div className="col-span-2 flex items-center gap-2 justify-center">
                          <input
                            data-testid={`pf-colour-swatch-${i}`}
                            type="color"
                            value={c.swatch}
                            onChange={(e) => updateAt(i, { swatch: e.target.value, border: e.target.value })}
                            className="w-9 h-9 rounded cursor-pointer bg-transparent border border-white/20"
                            title="Pick colour"
                          />
                        </div>
                        <button
                          type="button"
                          data-testid={`pf-colour-remove-${i}`}
                          onClick={() => removeColour(i)}
                          className="col-span-1 text-white/50 hover:text-red-500"
                          title="Remove"
                        >
                          <X className="w-4 h-4 mx-auto" />
                        </button>
                      </div>
                    ))}
                    <div className="flex flex-wrap justify-between gap-3 pt-3 border-t border-white/10">
                      <button type="button" data-testid="pf-colours-add" onClick={addColour} className="text-xs text-[#FF4500] uppercase tracking-widest hover:underline">
                        + Add colour
                      </button>
                      <button type="button" data-testid="pf-colours-load-default-2" onClick={loadDefault} className="text-xs text-white/50 uppercase tracking-widest hover:text-white">
                        Reset to default palette
                      </button>
                    </div>
                    <p className="text-[11px] text-white/40 pt-1">
                      Tip: the <strong>Code</strong> is auto-generated from the name — don't worry about it. Rename a colour and the code updates automatically. Hex must be a valid <code>#RRGGBB</code> value.
                    </p>
                  </div>
                )}
              </section>
            );
          })()}
        </div>

        {/* Sticky footer */}
        <div className="sticky bottom-0 z-10 bg-[#0F0F0F]/95 backdrop-blur border-t border-white/10 px-6 md:px-8 py-4 flex items-center justify-between gap-3">
          <p className="text-xs text-white/40 hidden md:block">Changes save immediately when you click Save.</p>
          <div className="flex gap-3">
            <button data-testid="pf-cancel" onClick={() => onOpenChange(false)} className="btn-ghost text-sm py-2 px-5">Cancel</button>
            <button data-testid="pf-save" onClick={save} disabled={saving} className="btn-primary text-sm py-2 px-6 disabled:opacity-50">
              {saving ? "Saving..." : isEdit ? "Save changes" : "Add product"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
